import EmptyState from '@/components/EmptyState';
import { ReportCard, ReportRow } from '@/features/reports/components/ReportCard';
import { useTopItems } from '@/features/reports/hooks/useReports';
import type { DateRange } from '@/features/reports/lib/range';
import { useT } from '@/i18n';
import { formatMoney } from '@/utils/money';
import { Block, Button, Preloader, Segmented } from 'framework7-react';
import { useState } from 'react';

/** What sold. Ranked by quantity or by revenue - the two questions an owner
 *  actually asks of a menu ("what moves" vs "what earns"). */
export function TopSellersSection({ range }: { range: DateRange }) {
  const t = useT();
  const ti = t.reports.topItems;
  const [rankBy, setRankBy] = useState<'QUANTITY' | 'REVENUE'>('QUANTITY');
  const { data, isLoading, error } = useTopItems(range, rankBy);
  const cur = data?.currency ?? 'USD';

  return (
    <>
      <div className="rpt-row">
        <ReportCard className="rpt-wide" title={ti.title}>
          <Segmented strong style={{ marginBottom: 4 }}>
            <Button active={rankBy === 'QUANTITY'} onClick={() => setRankBy('QUANTITY')}>
              {ti.byQuantity}
            </Button>
            <Button active={rankBy === 'REVENUE'} onClick={() => setRankBy('REVENUE')}>
              {ti.byRevenue}
            </Button>
          </Segmented>

          {isLoading && (
            <Block className="text-align-center">
              <Preloader />
            </Block>
          )}
          {error && (
            <EmptyState
              icon="exclamationmark_triangle"
              iconColor="#ff9500"
              title={ti.couldntLoad}
              description={(error as Error).message}
            />
          )}
          {data && data.items.length === 0 && (
            <EmptyState
              icon="chart_bar"
              title={ti.noSalesYet}
              description={ti.noItemsSoldInRange}
            />
          )}
          {data?.items.map((item, i) => (
            <ReportRow
              key={item.productId}
              label={`${i + 1}. ${item.name}`}
              sublabel={ti.sold(item.quantity)}
              value={formatMoney(item.revenueCents, cur)}
            />
          ))}
        </ReportCard>
      </div>
    </>
  );
}
